import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SummaryCardProps {
	icon: LucideIcon;
	/** The one value that identifies this record — a person's name. */
	title: string;
	/** Nothing filled in yet: dashed edge and a title that reads as an instruction. */
	isEmpty?: boolean;
	/** Puts the error on the card itself. The alert above says what is wrong; this says where. */
	hasError?: boolean;
	/** Trailing slot on the title row — an edit affordance. */
	action?: ReactNode;
	/** Second line: the fields an admin actually re-reads before moving on. */
	children?: ReactNode;
	className?: string;
}

/**
 * The read-only card that stands in for a form section the admin has already
 * filled in. Its job is a glance, not a record: show the fields that are worth
 * checking for a typo, and get out of the way.
 *
 * Shared so the patient contact and basic-information cards read as one set —
 * they sit directly above each other and answer the same question.
 */
export function SummaryCard({
	icon: Icon,
	title,
	isEmpty = false,
	hasError = false,
	action,
	children,
	className,
}: SummaryCardProps) {
	return (
		<div
			className={cn(
				"flex items-start gap-3 p-3 border rounded-md bg-muted/40 transition-colors",
				isEmpty ? "border-dashed" : "border-solid",
				hasError ? "border-destructive/50 bg-destructive/5" : "border-input",
				className,
			)}
		>
			<span
				className={cn(
					"flex items-center justify-center flex-none border rounded-md size-9 bg-background",
					hasError ? "border-destructive/30" : "border-black/10",
				)}
			>
				<Icon
					className={cn(
						"flex-shrink-0 size-4",
						hasError ? "text-destructive/75" : "text-muted-foreground/75",
					)}
				/>
			</span>

			<div className="grid flex-1 min-w-0 gap-1">
				<p
					className={cn(
						"truncate",
						isEmpty
							? "font-normal text-muted-foreground"
							: "font-semibold uppercase",
					)}
				>
					{title}
				</p>

				{children && (
					<div className="flex flex-wrap items-center text-xs gap-x-3 gap-y-1 text-muted-foreground">
						{children}
					</div>
				)}
			</div>

			{action && <div className="flex-none">{action}</div>}
		</div>
	);
}
